import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from '../utils/safeToast';
import { Plus, Loader2, Search, AlertTriangle } from 'lucide-react';

const API = "/api";

const PropertiesPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [cleaningCompanies, setCleaningCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    owner_id: '',
    cleaning_company_id: '',
    internal_rules: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [showIncompleteOnly]);

  const fetchData = async () => {
    try {
      const [propertiesRes, ownersRes, cleaningRes] = await Promise.all([
        axios.get(`${API}/properties`, { params: { incomplete_only: showIncompleteOnly } }),
        axios.get(`${API}/owners`),
        axios.get(`${API}/cleaning-companies`)
      ]);
      setProperties(propertiesRes.data);
      setOwners(ownersRes.data);
      setCleaningCompanies(cleaningRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Objekte');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.owner_id) {
      toast.error('Bitte wählen Sie einen Eigentümer');
      return;
    }
    if (!formData.cleaning_company_id) {
      toast.error('Bitte wählen Sie eine Reinigungsfirma');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/properties`, formData);
      toast.success('Objekt erfolgreich erstellt');
      setDialogOpen(false);
      setFormData({ name: '', street: '', city: '', owner_id: '', cleaning_company_id: '', internal_rules: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
    }
  };

  // Hilfsfunktion: Extrahiert den sortierbaren Teil des Namens (nach "I" oder "|")
  const getSortableName = (name) => {
    if (!name) return '';
    // Format: "001321 | Nordmarkstr. 20, W21" oder "001706 I Käpt'n-Christiansen-Str. 25, W6"
    // Regex: Suche nach Nummer + optionalem Buchstaben + Trennzeichen (I oder |)
    const match = name.match(/^[0-9]+[a-zA-Z]?\s*[I|]\s*/);
    if (match) {
      return name.substring(match[0].length).trim().toLowerCase();
    }
    return name.toLowerCase();
  };

  const filteredProperties = properties
    .filter(prop =>
      prop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (prop.street && prop.street.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (prop.city && prop.city.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      const nameA = getSortableName(a.name);
      const nameB = getSortableName(b.name);
      return nameA.localeCompare(nameB, 'de');
    });

  const incompleteCount = properties.filter(p => p.is_incomplete).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="properties-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Objekte</h1>
          <p className="text-stone-500 text-sm">{properties.length} Ferienwohnungen</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-full" data-testid="add-property-btn">
                <Plus className="w-4 h-4 mr-2" />
                Neues Objekt
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Neues Objekt anlegen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Objektname *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="z.B. Strandhaus Westerland"
                    required
                    className="bg-stone-50"
                    data-testid="property-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street">Straße *</Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    placeholder="Strandweg 1"
                    required
                    className="bg-stone-50"
                    data-testid="property-street-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ort *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="25980 Westerland"
                    required
                    className="bg-stone-50"
                    data-testid="property-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner">Eigentümer *</Label>
                  <Select
                    value={formData.owner_id}
                    onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                  >
                    <SelectTrigger className="bg-stone-50" data-testid="property-owner-select">
                      <SelectValue placeholder="Eigentümer wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {owners.map((owner) => (
                        <SelectItem key={owner.id} value={owner.id}>{owner.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cleaning">Reinigungsfirma *</Label>
                  <Select
                    value={formData.cleaning_company_id}
                    onValueChange={(value) => setFormData({ ...formData, cleaning_company_id: value })}
                  >
                    <SelectTrigger className="bg-stone-50" data-testid="property-cleaning-select">
                      <SelectValue placeholder="Reinigungsfirma wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {cleaningCompanies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full">
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                    disabled={submitting || owners.length === 0 || cleaningCompanies.length === 0}
                    data-testid="property-submit-btn"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Erstellen
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input
            placeholder="Objekte suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-stone-200"
            data-testid="property-search"
          />
        </div>
        {incompleteCount > 0 && (
          <Button
            variant={showIncompleteOnly ? "default" : "outline"}
            onClick={() => setShowIncompleteOnly(!showIncompleteOnly)}
            className={`rounded-full ${showIncompleteOnly ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
            data-testid="filter-incomplete-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Zuordnung fehlt ({incompleteCount})
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-stone-50">
              <TableRow>
                <TableHead className="font-semibold text-slate-900">Name</TableHead>
                <TableHead className="font-semibold text-slate-900">Straße</TableHead>
                <TableHead className="font-semibold text-slate-900">Ort</TableHead>
                <TableHead className="font-semibold text-slate-900">Eigentümer</TableHead>
                <TableHead className="font-semibold text-slate-900">Reinigungsfirma</TableHead>
                <TableHead className="font-semibold text-slate-900">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProperties.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-stone-500">
                    {searchTerm ? 'Keine Objekte gefunden' : 'Noch keine Objekte angelegt'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredProperties.map((property) => (
                  <TableRow
                    key={property.id}
                    className="cursor-pointer hover:bg-stone-50 transition-colors"
                    onClick={() => navigate(`/properties/${property.id}`)}
                    data-testid={`property-row-${property.id}`}
                  >
                    <TableCell className="font-medium text-slate-900">{property.name}</TableCell>
                    <TableCell className="text-stone-600">{property.street}</TableCell>
                    <TableCell className="text-stone-600">{property.city}</TableCell>
                    <TableCell className={property.owner_name ? 'text-stone-600' : 'text-amber-600'}>
                      {property.owner_name || '–'}
                    </TableCell>
                    <TableCell className={property.cleaning_company_name ? 'text-stone-600' : 'text-amber-600'}>
                      {property.cleaning_company_name || '–'}
                    </TableCell>
                    <TableCell>
                      {property.is_incomplete ? (
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">
                          Unvollständig
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                          Vollständig
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;
